<template>
	<div class="content-page content-page--code-manager">
		<div class="content-page__heading" ref="contentPageHeading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">Global Forms Code Manager</h2>
			</div>
			<div class="content-page__actions">
				<ButtonGroup>
					<template slot="container">
						<Button
							className="btn--has-icon"
							labelText="History"
							iconClassName="icon icon-history"
							:iconBeforeLabel="true"
							:btnClick="this.callCodeHistory"
						/>
						<MenuButton
							v-if="hasCreateCodePermission"
							variantName="primary"
							:items="codeAddOptions"
							v-model="codeAddValue"
							@change="addCode"
						/>
					</template>
				</ButtonGroup>
			</div>
		</div>
		<div
			class="results-toolbar results-toolbar--variant-two"
			ref="resultsToolBar"
		>
			<div class="results-toolbar__search">
				<Search
					variant="toggle-icons"
					v-model="filters.search"
					placeholder="Search by code, code ID, or description"
				/>
			</div>
			<div class="results-toolbar__sort">
				<DropdownSort
					:options="ddsOptions"
					:showClearSelectedButton="false"
					v-model="ddsValue"
				/>
			</div>
			<div class="results-toolbar__counter">
				<ResultCounter v-if="hasResults" :counter="resultCounter" />
			</div>
		</div>
		<ScrollContainer
			v-if="hasResults"
			variant="infinity-scroll"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
		>
			<CodeManagerCodeList :items="codeManager.items" />
		</ScrollContainer>
		<Spinner
			v-if="loading"
			:processor="{ loading: true }"
			data-test="spinner"
		/>
		<NoResults v-if="!loading && !hasResults" data-test="noResults" />
		<CodeListModal :search="filters.search" v-model="showCodeListModal" />
		<CodeNewCopyModal v-model="showNewCodeModal" />
		<CodeDetailsModal
			:display="showCodeDetailModal"
			:snippet="codeSelectedDetails"
		/>
		<CodeManagerFormsModal
			:display="showFormsModal"
			:forms="codeSelectedDetails.forms"
		/>
	</div>
</template>

<script>
import CodeManagerCodeList from './snippets-summary-list';
import CodeNewCopyModal from '@/views/code-manager/new-copy-of-code-settings-modal.vue';
import CodeListModal from '@/views/code-manager/code-list-modal.vue';
import Search from '@/components/forms/search/search';
import DropdownSort from '@/components/forms/dropdown/dropdown-sort';
import ButtonGroup from '@/components/forms/button/button-group';
import Button from '@/components/forms/button/button';
import MenuButton from '@/components/forms/button/menu-button';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import { mapActions, mapState } from 'vuex';
import { isEmpty } from '@/utils/object';
import CodeDetailsModal from '@/views/form-designer/code-details-modal.vue';
import CodeManagerFormsModal from '@/views/code-manager/code-manager-forms-modal';
//;
import ScrollContainer from '@/components/shells/scroll-container/scroll-container.vue';
import Spinner from '@/components/visual/spinner/spinner';
import NoResults from '@/site-components/no-results/no-results';
import { LoggingMixin } from 'vue-ui-layout';

import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';

import _ from 'lodash';

export default {
	name: 'GlobalFormCodeManager',
	mixins: [LoggingMixin],
	components: {
		Search,
		DropdownSort,
		ButtonGroup,
		Button,
		MenuButton,
		ResultCounter,
		CodeNewCopyModal,
		CodeListModal,
		Breadcrumbs,
		CodeDetailsModal,
		CodeManagerFormsModal,
		CodeManagerCodeList,
		ScrollContainer,
		Spinner,
		NoResults
	},
	props: {
		search: String,
		sortBy: { type: String },
		sortDirection: { type: String }
	},
	data() {
		return {
			filters: {},
			page: 1,
			countPerPage: this.count,
			defaultFilters: {
				search: undefined,
				sortBy: 'Id',
				sortDirection: 'asc'
			},
			ddsOptions: [
				{ text: 'Code ID', value: 'Id' },
				{ text: 'Code Description', value: 'description' }
			],
			codeAddValue: {
				text: 'New Code',
				value: 'default'
			},
			codeAddOptions: [
				{
					text: 'New Code',
					value: 'N'
				},
				{
					text: 'Copy of',
					value: 'C'
				}
			],
			showNewCodeModal: false,
			showCodeListModal: false,
			loading: true,
			loadingMore: false,
			reloaded: false,
			defaultUrlNames: [
				{
					url: `/forms`,
					name: 'Global Forms'
				},
				{
					name: 'Code Manager'
				}
			]
		};
	},
	computed: {
		...mapState('codemanager', [
			'codeManager',
			'codeSelected',
			'showCodeDetailModal',
			'codeSelectedDetails',
			'showFormsModal'
		]),
		ddsValue: {
			get() {
				return {
					sortby: this.filters.sortBy,
					sortdirection: this.filters.sortDirection
				};
			},
			set(value) {
				this.filters.sortBy = value.sortby;
				this.filters.sortDirection = value.sortdirection;
			}
		},
		hasCreateCodePermission() {
			return this.validatePermission('snippetsDraft.create');
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);

			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}

			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.defaultUrlNames;
		},
		hasResults() {
			return this.codeManager.items?.length && !this.loading;
		},
		resultCounter() {
			return `${this.codeManager.items?.length} of ${this.codeManager.totalCount}`;
		},
		hasMoreItemsToLoad() {
			return _.isEmpty(this.codeManager)
				? false
				: this.page < this.codeManager.totalPageCount;
		}
	},
	methods: {
		...mapActions('codemanager', ['loadCodeManager']),
		async fetchData() {
			if (!this.loadingMore) {
				this.loading = true;
			}

			let query = removeObjectEmptyValues(this.filters);

			query.page = this.page;
			query.count = 100;

			try {
				await this.loadCodeManager({ ...query, saveOnStore: true });
			} catch (error) {
				console.log(error);
			} finally {
				this.loading = false;
				this.loadingMore = false;
			}
		},
		callCodeHistory() {
			this.$router.push({
				path: '/forms/code/history/'
			});
		},
		addCode(obj) {
			if (obj.value === 'N') {
				this.showCodeListModal = false;
				this.$store.commit('codemanager/SetCodeSelected', {});
				this.showNewCodeModal = true;
				return;
			}

			this.$store.commit('codemanager/SetCodeManager', this.codeManager);
			this.showNewCodeModal = false;
			this.showCodeListModal = true;
			this.codeAddValue = {
				text: 'New Code',
				value: 'default'
			};
		},

		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.loadingMore = true;
				this.page++;
				this.fetchData();
			}
		},
		updateRoute() {
			let queries = removePropertiesWithSameValues(
				this.filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);

			this.$router
				.push({
					path: '/forms/code',
					query: queries
				})
				.catch(() => {});
		}
	},
	watch: {
		filters: {
			deep: true,
			handler() {
				this.page = 1;
				this.updateRoute();
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				this.page = 1;

				this.filters = { ...this.defaultFilters, ...newQuery };

				this.fetchData();
			}
		},
		reloadCodeManager: {
			handler(reload) {
				if (reload) {
					this.updateRoute(this.defaultFilters);
				}
			}
		},
		codeSelected: {
			deep: true,
			handler(val) {
				if (!isEmpty(val)) {
					if (
						val.sourceOfSelection === 'CopyOfListing' ||
						val.sourceOfSelection === 'CodeManagerCard'
					) {
						this.showCodeListModal = false;
						this.showNewCodeModal = true;
					} else {
						this.showNewCodeModal = false;
					}
				}
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		const updateBreadcrumb = (to, from, breadcrumbMapping) => {
			let actualBreadcrumb =
				from.meta && from.meta.breadcrumb
					? [...from.meta.breadcrumb]
					: [];

			const defaultBreadcrumb = [
				{ name: 'Global Forms', data: { path: '/forms' } },
				{ name: 'Code Manager', data: { path: '/forms/code' } }
			];

			if (actualBreadcrumb.length === 0) {
				actualBreadcrumb = defaultBreadcrumb;
			}

			const codePageIndex = actualBreadcrumb.findIndex(
				obj => obj.name === 'Code Manager' && !obj.url
			);

			if (codePageIndex !== -1) {
				actualBreadcrumb[codePageIndex].url = from.fullPath;
			}

			const getBreadcrumbName = key => breadcrumbMapping[key] || key;
			const breadcrumbName = getBreadcrumbName(to.name);
			actualBreadcrumb = [...actualBreadcrumb, { name: breadcrumbName }];

			to.meta.breadcrumb = actualBreadcrumb;

			const obj = { path: to.fullPath, breadcrumb: actualBreadcrumb };
			this.$store.commit('setBreadCrumbs', obj);
		};

		if (to.name === 'code-history') {
			const breadcrumbMapping = { 'code-history': 'History' };
			updateBreadcrumb(to, from, breadcrumbMapping);
		}

		if (
			to.name === 'impact-form-edit-designer' ||
			to.name === 'laser-form-edit-designer'
		) {
			const breadcrumbMapping = {
				'impact-form-edit-designer': 'Form Designer',
				'laser-form-edit-designer': 'Form Designer'
			};
			updateBreadcrumb(to, from, breadcrumbMapping);
		}

		next();
	}
};
</script>

<style lang="scss" scoped>
/**
  * Global Forms Code Manager Header
**/

.results-toolbar__search {
	@include mq($from: md) {
		width: 310px;
	}
}
.content-page {
	&__actions {
		@include mq($until: md) {
			margin-top: $gutter-md;
		}
		.btn-set {
			display: inline-flex;
		}
	}
}
</style>
